import { Controller } from "@hotwired/stimulus";
import {
  createMap,
  fitBounds,
  createLotLabel,
  LOT_LABEL_STYLE,
} from "../../helpers/map_helpers";

const DEFAULT_STYLE = {
  color: "#3B82F6",
  weight: 2,
  fillColor: "#3B82F6",
  fillOpacity: 0.2,
};

const SELECTED_STYLE = {
  color: "#6B21A8",
  weight: 3,
  fillColor: "#6B21A8",
  fillOpacity: 0.3,
};

const SELECTED_LABEL_STYLE = `
  background: rgba(107, 33, 168, 0.9);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  border: 1px solid rgba(107, 33, 168, 1);
  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
  white-space: nowrap;
  pointer-events: none;
`;

const COMPACT_LABEL_STYLE = `
  background: rgba(59, 130, 246, 0.85);
  color: white;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-size: 9px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.4);
  pointer-events: none;
`;

const SELECTED_COMPACT_LABEL_STYLE = `
  background: rgba(107, 33, 168, 0.95);
  color: white;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.5);
  pointer-events: none;
`;

const EXCLUDED_STYLE = {
  color: "#6B7280",
  weight: 2,
  fillColor: "#6B7280",
  fillOpacity: 0.35,
  dashArray: "6 4",
};

const EXCLUDED_LABEL_STYLE = `
  background: rgba(75, 85, 99, 0.85);
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  text-decoration: line-through;
  border: 1px solid rgba(75, 85, 99, 1);
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  white-space: nowrap;
  pointer-events: none;
`;

const EXCLUDED_COMPACT_LABEL_STYLE = `
  background: rgba(75, 85, 99, 0.8);
  color: white;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-size: 9px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
  pointer-events: none;
  text-decoration: line-through;
`;

// Connects to data-controller="lots--bulk-import"
export default class extends Controller {
  static targets = [
    "map",
    "row",
    "varietySelect",
    "treesInput",
    "reseedingsInput",
    "nameInput",
    "toolbar",
    "varietyTemplate",
    "bulkVarietySelect",
    "excludeCheckbox",
    "lotCount",
    "areaTotal",
    "areaInput",
  ];
  static values = { features: Array };

  connect() {
    this.selectedIndex = -1;
    this.excluded = new Set();
    this.stripeLayers = [];
    this.initMap();
  }

  initMap() {
    if (!this.hasMapTarget || !this.featuresValue.length) return;

    this.map = createMap(this.mapTarget);

    this.layers = [];
    this.labels = [];

    const allLayers = [];

    this.featuresValue.forEach((feature, index) => {
      const layer = L.geoJSON(feature.polygon_geojson, {
        style: DEFAULT_STYLE,
      }).addTo(this.map);

      const name = feature.name || `Lot ${index + 1}`;
      const label = createLotLabel(this.map, name, layer);

      layer.on("click", () =>
        this.selectRow({
          currentTarget: this.rowTargets[index],
          params: { index },
        }),
      );
      layer.eachLayer((l) => allLayers.push(l));

      this.layers.push(layer);
      this.labels.push(label);
    });

    if (allLayers.length) {
      fitBounds(this.map, allLayers, { padding: [20, 20] });
    }

    // Switch between compact (number) and full (name) labels based on zoom
    this.nameZoomThreshold = 16;
    this.map.on("zoomend", () => this.updateLabelMode());
    this.updateLabelMode();
  }

  updateLabelMode() {
    const zoom = this.map.getZoom();
    const showFullName = zoom >= this.nameZoomThreshold;

    this.labels.forEach((label, i) => {
      const el = label.getElement();
      if (!el) return;

      const name = this.featuresValue[i]?.name || `Lot ${i + 1}`;
      const number = i + 1;
      const isExcluded = this.excluded.has(i);
      const isSelected = i === this.selectedIndex && !isExcluded;

      el.style.opacity = "";

      if (showFullName) {
        const style = isExcluded
          ? EXCLUDED_LABEL_STYLE
          : isSelected
            ? SELECTED_LABEL_STYLE
            : LOT_LABEL_STYLE;
        const div = document.createElement("div");
        div.setAttribute("style", style);
        div.textContent = name;
        el.replaceChildren(div);
      } else {
        const compactStyle = isExcluded
          ? EXCLUDED_COMPACT_LABEL_STYLE
          : isSelected
            ? SELECTED_COMPACT_LABEL_STYLE
            : COMPACT_LABEL_STYLE;
        const div = document.createElement("div");
        div.setAttribute("style", compactStyle);
        div.textContent = number;
        el.replaceChildren(div);
      }
    });
  }

  selectRow(event) {
    const row = event.currentTarget;
    if (!row) return;

    const index = parseInt(row.dataset.index);
    this.selectedIndex = index;

    // Highlight row
    this.rowTargets.forEach((r) => r.classList.remove("bg-primary/10"));
    row.classList.add("bg-primary/10");

    // Scroll row into view
    row.scrollIntoView({ behavior: "smooth", block: "nearest" });

    // Reset all layers to default, highlight selected, respect excluded
    this.layers.forEach((layer, i) => {
      if (this.excluded.has(i)) {
        layer.setStyle(EXCLUDED_STYLE);
      } else if (i === index) {
        layer.setStyle(SELECTED_STYLE);
        layer.bringToFront();
      } else {
        layer.setStyle(DEFAULT_STYLE);
      }
    });

    // Update labels (respects current zoom level for compact vs full)
    this.updateLabelMode();

    // Pan to polygon
    const targetLayer = this.layers[index];
    if (targetLayer) {
      const layerBounds = targetLayer.getBounds();
      if (layerBounds.isValid()) {
        this.map.fitBounds(layerBounds, { padding: [50, 50], maxZoom: 17 });
      }
    }
  }

  ensureStripePattern() {
    if (this._stripePatternAdded) return;

    const svg = this.map.getRenderer(this.layers[0])._container;
    let defs = svg.querySelector("defs");
    if (!defs) {
      defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
      svg.insertBefore(defs, svg.firstChild);
    }

    const pattern = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "pattern",
    );
    pattern.setAttribute("id", "excluded-stripes");
    pattern.setAttribute("width", "10");
    pattern.setAttribute("height", "10");
    pattern.setAttribute("patternUnits", "userSpaceOnUse");
    pattern.setAttribute("patternTransform", "rotate(45)");

    const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
    line.setAttribute("x1", "0");
    line.setAttribute("y1", "0");
    line.setAttribute("x2", "0");
    line.setAttribute("y2", "10");
    line.setAttribute("stroke", "rgba(75, 85, 99, 0.6)");
    line.setAttribute("stroke-width", "4");

    pattern.appendChild(line);
    defs.appendChild(pattern);
    this._stripePatternAdded = true;
  }

  addStripeOverlay(index) {
    this.ensureStripePattern();
    const feature = this.featuresValue[index];
    if (!feature) return;

    const stripeLayer = L.geoJSON(feature.polygon_geojson, {
      style: {
        stroke: false,
        fillOpacity: 1,
        fillColor: "url(#excluded-stripes)",
        className: "excluded-stripe-fill",
      },
      interactive: false,
    }).addTo(this.map);

    // Leaflet doesn't support url() fill via style option, so patch the SVG path directly
    stripeLayer.eachLayer((l) => {
      const path = l.getElement();
      if (path) {
        path.setAttribute("fill", "url(#excluded-stripes)");
        path.setAttribute("fill-opacity", "1");
        path.setAttribute("stroke", "none");
        path.style.pointerEvents = "none";
      }
    });

    this.stripeLayers[index] = stripeLayer;
  }

  removeStripeOverlay(index) {
    const stripeLayer = this.stripeLayers[index];
    if (stripeLayer) {
      this.map.removeLayer(stripeLayer);
      this.stripeLayers[index] = null;
    }
  }

  toggleExclude(event) {
    const index = parseInt(event.target.dataset.index, 10);
    const isExcluded = event.target.checked;

    if (isExcluded) {
      this.excluded.add(index);
    } else {
      this.excluded.delete(index);
    }

    // Update polygon style and stripe overlay
    const layer = this.layers[index];
    if (layer) {
      if (isExcluded) {
        layer.setStyle(EXCLUDED_STYLE);
        this.addStripeOverlay(index);
      } else {
        const style =
          index === this.selectedIndex ? SELECTED_STYLE : DEFAULT_STYLE;
        layer.setStyle(style);
        this.removeStripeOverlay(index);
      }
    }

    // Update label (respects zoom level)
    this.updateLabelMode();

    // Dim the table row and toggle required validation
    const row = this.rowTargets[index];
    if (row) {
      row.classList.toggle("opacity-40", isExcluded);
      row
        .querySelectorAll("input[type='text'], input[type='number'], select")
        .forEach((el) => {
          if (isExcluded) {
            el.removeAttribute("required");
            el.removeAttribute("min");
          } else {
            // Restore required/min only on fields that originally had them
            if (
              el.name.includes("[name]") ||
              el.name.includes("[crop_variety_id]") ||
              el.name.includes("[total_trees]")
            ) {
              el.setAttribute("required", "");
            }
            if (el.name.includes("[total_trees]")) {
              el.setAttribute("min", "1");
            }
            if (el.name.includes("[total_reseedings]")) {
              el.setAttribute("min", "0");
            }
          }
        });
    }

    this.updateStats();
  }

  updateStats() {
    const total = this.featuresValue.length;
    const excludedCount = this.excluded.size;
    const kept = total - excludedCount;

    if (this.hasLotCountTarget) {
      this.lotCountTarget.textContent = kept;
    }

    if (this.hasAreaTotalTarget) {
      let totalHa = 0;
      this.areaInputTargets.forEach((input, i) => {
        if (!this.excluded.has(i)) {
          totalHa += parseFloat(input.value) || 0;
        }
      });
      const formatted = new Intl.NumberFormat(
        document.documentElement.lang || "es",
      ).format(totalHa.toFixed(2));
      this.areaTotalTarget.textContent = `${formatted} ha`;
    }
  }

  openVarietyModal() {
    const modal = document.getElementById("create_crop_variety_modal");
    if (modal) modal.showModal();
  }

  assignVarietyToAll(event) {
    const value = event.target.value;
    if (!value) return;

    this.varietySelectTargets.forEach((select) => {
      select.value = value;
    });
  }

  assignTreesToAll(event) {
    const value = event.target.value;
    if (!value) return;

    this.treesInputTargets.forEach((input) => {
      input.value = value;
    });
  }

  assignReseedingsToAll(event) {
    const value = event.target.value;

    this.reseedingsInputTargets.forEach((input) => {
      input.value = value;
    });
  }

  // Stimulus target callback — fires when turbo_stream.replace swaps the template
  varietyTemplateTargetConnected() {
    if (!this._initialVarietyLoad) {
      this._initialVarietyLoad = true;
      return; // Skip the first connect (initial page render)
    }

    const newOptions = this.varietyTemplateTarget.innerHTML;

    // Update all row selects
    this.varietySelectTargets.forEach((select) => {
      const currentValue = select.value;
      select.innerHTML = newOptions;
      select.value = currentValue;
    });

    // Update bulk assign select (preserve its placeholder)
    if (this.hasBulkVarietySelectTarget) {
      const bulkSelect = this.bulkVarietySelectTarget;
      const currentValue = bulkSelect.value;
      const placeholder =
        bulkSelect.querySelector("option[value='']")?.textContent;
      bulkSelect.innerHTML = newOptions;
      const firstOption = bulkSelect.querySelector("option[value='']");
      if (firstOption && placeholder) firstOption.textContent = placeholder;
      bulkSelect.value = currentValue;
    }

    // Close the modal
    document.getElementById("create_crop_variety_modal")?.close();
  }

  disconnect() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }
}
